/**
 * Service Request Intake Modal
 *
 * Creates a new service request (Stage 01-Intake).
 * Captures: customer, optional linked service, request type, title, description,
 * priority, intake method.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Save,
  Loader,
  Search,
  Phone,
  Mail,
  MessageCircle,
  Edit3,
  Plus,
  Wifi,
  CheckCircle2,
} from 'lucide-react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/firebase';
import { useServiceRequestTypes } from '@/hooks/useTradePack';
import { useTeam } from '@/hooks/useTeam';
import { useModalAccessibility } from '@/hooks/useModalAccessibility';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';
import { createServiceRequest, notifyClosureOwners } from '@/services/service-request.service';
import { getCustomerServices } from '@/services/customer-service.service';
import { makeCustomerMatcher } from '@/utils/customer-search';
import type { Customer, CustomerService } from '@/types/schema';
import type {
  ServiceRequestType,
  ServiceRequestTypeKey,
  ServiceRequestPriority,
  ServiceRequestIntakeMethod,
  ServiceRequestClosure,
  ServiceRequestClosurePart,
} from '@/types/service-request.types';
import {
  SERVICE_REQUEST_PRIORITY_LABELS,
  SERVICE_REQUEST_PRIORITY_COLORS,
  SERVICE_REQUEST_CLOSURE_PARTS,
  getInitialStage,
} from '@/types/service-request.types';
import { AddCustomerServiceModal } from '@/components/AddCustomerServiceModal';
import { useFeatureGate } from '@/hooks/useFeatureGate';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (requestId: string) => void;
  /** When provided, the customer is fetched and selected on open instead of
   *  forcing manual search — mirrors FaultIntakeModal / DealCreationWizard.
   *  Routes through the same selection path so the services picker loads. */
  preSelectedCustomerId?: string;
  /** Optional starting values — used by Send-to-Service-Request conversions
   *  (from a fault) to carry context across. Applied on open only. */
  seed?: {
    title?: string;
    description?: string;
    priority?: ServiceRequestPriority;
    requestType?: ServiceRequestType;
    serviceId?: string;
  };
}

const SEARCH_DEBOUNCE = 300;

const INTAKE_METHOD_OPTIONS: Array<{
  value: ServiceRequestIntakeMethod;
  label: string;
  icon: typeof Phone;
}> = [
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'sms', label: 'SMS', icon: MessageCircle },
  { value: 'manual', label: 'Manual', icon: Edit3 },
];


const PRIORITIES: ServiceRequestPriority[] = ['low', 'medium', 'high', 'urgent'];

export function ServiceRequestIntakeModal({ isOpen, onClose, onCreated, preSelectedCustomerId, seed }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Customer search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set when searchQuery is populated programmatically (customer selected /
  // pre-selected) so the debounced search doesn't re-open the dropdown.
  const suppressNextSearchRef = useRef(false);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Service picker
  const [services, setServices] = useState<CustomerService[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [servicesLoading, setServicesLoading] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);

  // Escape-to-close + focus trap, matching FaultIntakeModal / DealCreationWizard
  // (this modal previously had neither, so Escape fell through to whatever
  // modal was underneath — from CustomerDetailModal that closed the customer
  // record and left this form orphaned). Suspended while the nested
  // AddCustomerServiceModal is stacked on top; both listen on document, and
  // document listeners can't stopPropagation each other.
  const modalRef = useModalAccessibility({
    isOpen,
    onClose,
    escapeDisabled: showAddServiceModal,
  });

  // Form fields
  const [requestType, setRequestType] = useState<ServiceRequestTypeKey>('');

  // Request types come from the tenant's trade pack, so the default can't be a
  // literal: 'speed_plan_change' means nothing to a gate installer. The pack
  // also arrives in two steps — `useServiceRequestTypes()` returns the static
  // telco list synchronously, then swaps when the Firestore subscription
  // resolves — so a type chosen against the fallback can stop existing a beat
  // later. This effect covers both: pick the first type when nothing is
  // selected, and drop a selection the active pack doesn't offer.
  const { types: requestTypes } = useServiceRequestTypes();
  useEffect(() => {
    if (requestTypes.length === 0) return;
    if (!requestType || !requestTypes.some((t) => t.key === requestType)) {
      setRequestType(requestTypes[0].key);
    }
  }, [requestTypes, requestType]);
  const [intakeMethod, setIntakeMethod] = useState<ServiceRequestIntakeMethod>('phone');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<ServiceRequestPriority>('medium');

  // Relocating fields (requestType === 'relocating')
  const [relocNewAddress, setRelocNewAddress] = useState('');
  const [relocMoveDate, setRelocMoveDate] = useState('');
  const [relocWho, setRelocWho] = useState('');
  const [relocOldAddressPlan, setRelocOldAddressPlan] = useState('');

  // Account closure fields (requestType === 'account_closure'). Owners are
  // user ids keyed by part; '' / absent = nobody yet.
  const { team } = useTeam();
  const closureCandidates = team.filter((m) => m.active !== false);
  const [closureDate, setClosureDate] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const [closureOwners, setClosureOwners] = useState<Partial<Record<ServiceRequestClosurePart, string>>>({});

  // Reset on close
  useEffect(() => {
    if (isOpen) return;
    setSearchQuery('');
    setSearchResults([]);
    setSelectedCustomer(null);
    setServices([]);
    setSelectedServiceId('');
    setShowAddServiceModal(false);
    setRequestType('speed_plan_change');
    setIntakeMethod('phone');
    setTitle('');
    setDescription('');
    setPriority('medium');
    setRelocNewAddress('');
    setRelocMoveDate('');
    setRelocWho('');
    setRelocOldAddressPlan('');
    setClosureDate('');
    setClosureReason('');
    setClosureOwners({});
    setError(null);
  }, [isOpen]);

  // Pre-selection + seeding — when launched from a conversion (Send to
  // Service Request on a fault) or a customer quick-action, fetch the
  // customer and hydrate the form. Cancellable so a fast reopen with a
  // different id can't race a stale fetch. Mirrors FaultIntakeModal.
  useEffect(() => {
    if (!isOpen) return;

    if (seed) {
      if (seed.title) setTitle((prev) => (prev.trim() ? prev : seed.title!));
      if (seed.description) setDescription((prev) => (prev.trim() ? prev : seed.description!));
      if (seed.priority) setPriority(seed.priority);
      if (seed.requestType) setRequestType(seed.requestType);
    }

    if (!preSelectedCustomerId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'customers', preSelectedCustomerId));
        if (cancelled || !snap.exists()) return;
        const c = { id: snap.id, ...snap.data() } as Customer;
        // Route through the normal selection path so the services picker loads
        suppressNextSearchRef.current = true;
        setSelectedCustomer(c);
        setSearchQuery(c.name || '');
        setSearchOpen(false);
        if (seed?.serviceId) setSelectedServiceId(seed.serviceId);
      } catch (err) {
        console.warn('[ServiceRequestIntakeModal] preSelectedCustomerId fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
     
  }, [isOpen, preSelectedCustomerId]);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'customers'),
        where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      ));
      // Shared matcher — same comprehensive field set + digit-aware phone
      // matching as the New Deal / New Fault pickers (was a narrow
      // name/email/phone filter that missed company/account/address lookups
      // and reformatted phone numbers).
      const match = makeCustomerMatcher(q);
      const results = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as Customer))
        .filter(match)
        .slice(0, 8);
      setSearchResults(results);
      setSearchOpen(results.length > 0);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (suppressNextSearchRef.current) {
      suppressNextSearchRef.current = false;
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      doSearch(searchQuery);
    }, SEARCH_DEBOUNCE);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, doSearch]);

  // Load services when customer is selected
  const loadServices = useCallback(
    async (customerId: string): Promise<CustomerService[]> => {
      setServicesLoading(true);
      try {
        const s = await getCustomerServices(customerId);
        setServices(s);
        return s;
      } catch (err) {
        console.error('Error loading customer services:', err);
        setServices([]);
        return [];
      } finally {
        setServicesLoading(false);
      }
    },
    []
  );

  // Service requests are a `standard` feature but customer services are `msp`
  // — for a tenant without that pack, skip the fetch and hide the picker
  // entirely (selectedServiceId simply stays ''). Otherwise the intake form
  // would offer to create a customer_services row they can never see again.
  const hasCustomerServices = useFeatureGate('customer-services');

  useEffect(() => {
    if (!selectedCustomer?.id || !hasCustomerServices) {
      setServices([]);
      setSelectedServiceId('');
      return;
    }
    loadServices(selectedCustomer.id);
  }, [selectedCustomer, loadServices, hasCustomerServices]);

  const handleSelectCustomer = (customer: Customer) => {
    suppressNextSearchRef.current = true;
    setSelectedCustomer(customer);
    setSearchQuery(customer.name || '');
    setSearchOpen(false);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!selectedCustomer || !selectedCustomer.id) {
      setError('Please select a customer');
      return;
    }
    if (!title.trim()) {
      setError('Please enter a short title for this request');
      return;
    }

    setLoading(true);
    try {
      const userId = localStorage.getItem('user_id') || 'unknown';
      const userName = localStorage.getItem('logged_in_user') || 'Office Team';

      let closure: ServiceRequestClosure | undefined;
      if (requestType === 'account_closure') {
        const parts: ServiceRequestClosure['parts'] = {};
        for (const { key } of SERVICE_REQUEST_CLOSURE_PARTS) {
          const owner = team.find((m) => m.id === closureOwners[key]);
          parts[key] = owner ? { assigneeId: owner.id, assigneeName: owner.name } : {};
        }
        closure = {
          ...(closureDate ? { effectiveDate: closureDate } : {}),
          ...(closureReason.trim() ? { reason: closureReason.trim() } : {}),
          parts,
        };
      }

      const selectedService = selectedServiceId
        ? services.find((s) => s.id === selectedServiceId)
        : undefined;

      const id = await createServiceRequest({
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name || 'Unknown',
        // Fall back to the raw selected id when the services list hasn't
        // finished loading yet (seeded conversions can submit before the
        // async getCustomerServices resolves) — never silently drop the link.
        serviceId: selectedService?.id ?? (selectedServiceId || undefined),
        serviceType: selectedService?.serviceType,
        servicePlanName: selectedService?.planName,
        requestType,
        title: title.trim(),
        description: description.trim(),
        priority,
        intakeMethod,
        intakeBy: userId,
        intakeByName: userName,
        ...(requestType === 'relocating' &&
        (relocNewAddress.trim() || relocMoveDate || relocWho.trim() || relocOldAddressPlan.trim())
          ? {
              relocating: {
                ...(relocNewAddress.trim() ? { newAddress: relocNewAddress.trim() } : {}),
                ...(relocMoveDate ? { moveDate: relocMoveDate } : {}),
                ...(relocWho.trim() ? { who: relocWho.trim() } : {}),
                ...(relocOldAddressPlan.trim() ? { oldAddressPlan: relocOldAddressPlan.trim() } : {}),
              },
            }
          : {}),
        ...(closure ? { closure } : {}),
      });

      // The request is saved at this point — a failed alert must not lose it
      // or keep the form open, but it mustn't vanish silently either.
      if (closure) {
        try {
          await notifyClosureOwners(
            {
              requestId: id,
              customerName: selectedCustomer.name || 'Unknown',
              title: title.trim(),
              tenantId: getTenantIdSync(),
            },
            getInitialStage(requestType),
            closure.parts,
            { id: userId, name: userName },
          );
        } catch (alertErr) {
          console.error('[ServiceRequestIntakeModal] closure owner alerts failed:', alertErr);
        }
      }

      onCreated?.(id);
      onClose();
    } catch (err: unknown) {
      console.error('Error creating service request:', err);
      setError((err as { message?: string })?.message || 'Failed to create service request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="New Service Request"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-surface border border-theme rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-theme">
              <div>
                <h2 className="text-xl font-bold text-theme-primary">New Service Request</h2>
                <p className="text-xs text-theme-muted mt-0.5">
                  Log a customer-initiated service change
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-surface-hover rounded-lg text-theme-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Customer picker */}
              <div>
                <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                  Customer *
                </label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (selectedCustomer) setSelectedCustomer(null);
                    }}
                    onFocus={() => {
                      if (searchResults.length > 0) setSearchOpen(true);
                    }}
                    placeholder="Search customer by name, email, or phone..."
                    className="w-full pl-9 pr-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                  />
                  {searchLoading && (
                    <Loader className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-theme-muted animate-spin" />
                  )}
                  {searchOpen && searchResults.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-surface border border-theme rounded-lg shadow-xl max-h-60 overflow-y-auto z-10">
                      {searchResults.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => handleSelectCustomer(c)}
                          className="w-full px-4 py-2.5 text-left hover:bg-surface-hover transition-colors border-b border-theme last:border-b-0"
                        >
                          <div className="text-sm font-medium text-theme-primary">
                            {c.name}
                          </div>
                          <div className="text-xs text-theme-muted">
                            {c.phone || c.email || c.address || '—'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedCustomer && (
                  <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                    Selected: {selectedCustomer.name}
                  </div>
                )}
              </div>

              {/* Optional service picker — MSP pack only (see above) */}
              {selectedCustomer && hasCustomerServices && (
                <div>
                  <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                    Link to Service (optional)
                  </label>
                  {servicesLoading ? (
                    <div className="px-3 py-4 bg-surface-card border border-theme rounded-lg text-xs text-theme-muted flex items-center gap-2">
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                      Loading services…
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {/* No specific service — always visible */}
                      <button
                        type="button"
                        onClick={() => setSelectedServiceId('')}
                        className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-all border-2 ${
                          selectedServiceId === ''
                            ? 'bg-brand-secondary/15 border-brand-secondary text-theme-primary'
                            : 'bg-surface-hover border-theme text-theme-secondary hover:bg-surface-raised'
                        }`}
                      >
                        <div className="w-7 h-7 rounded-md bg-surface-card border border-theme flex items-center justify-center flex-shrink-0">
                          <X className="w-3.5 h-3.5 text-theme-muted" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">No Specific Service</div>
                          <div className="text-[10px] text-theme-muted">
                            Not tied to a particular service
                          </div>
                        </div>
                        {selectedServiceId === '' && (
                          <CheckCircle2 className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                        )}
                      </button>

                      {/* Existing services */}
                      {services.map((s) => {
                        const isSelected = selectedServiceId === s.id;
                        const statusTone =
                          s.status === 'Active'
                            ? 'text-emerald-500 dark:text-emerald-400'
                            : s.status === 'Suspended'
                            ? 'text-amber-500 dark:text-amber-400'
                            : s.status === 'Cancelled'
                            ? 'text-rose-500 dark:text-rose-400'
                            : 'text-theme-muted';
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setSelectedServiceId(s.id!)}
                            className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-all border-2 ${
                              isSelected
                                ? 'bg-brand-secondary/15 border-brand-secondary text-theme-primary'
                                : 'bg-surface-hover border-theme text-theme-secondary hover:bg-surface-raised'
                            }`}
                          >
                            <div className="w-7 h-7 rounded-md bg-brand-secondary/10 border border-brand-secondary/30 flex items-center justify-center flex-shrink-0">
                              <Wifi className="w-3.5 h-3.5 text-brand-secondary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">
                                {s.serviceType}
                                {s.planName ? ` — ${s.planName}` : ''}
                              </div>
                              {s.status && (
                                <div className={`text-[10px] ${statusTone}`}>{s.status}</div>
                              )}
                            </div>
                            {isSelected && (
                              <CheckCircle2 className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}

                      {/* Add new service */}
                      <button
                        type="button"
                        onClick={() => setShowAddServiceModal(true)}
                        className="relative flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-all border-2 border-dashed border-emerald-400/40 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/15 hover:border-emerald-400/60"
                      >
                        <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                          <Plus className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">Add New Service</div>
                          <div className="text-[10px] opacity-80">
                            Create a service for this customer
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                  {services.length === 0 && !servicesLoading && (
                    <p className="text-[11px] text-theme-muted mt-2">
                      This customer has no services on record yet — add one or log without
                      linking to a service.
                    </p>
                  )}
                </div>
              )}

              {/* Request type */}
              <div>
                <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                  Request Type *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {requestTypes.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setRequestType(t.key)}
                      aria-pressed={requestType === t.key}
                      className={`px-3 py-2.5 rounded-lg text-sm text-left transition-all border-2 ${
                        requestType === t.key
                          ? 'bg-brand-secondary/15 border-brand-secondary text-theme-primary'
                          : 'bg-surface-hover border-theme text-theme-secondary hover:bg-surface-raised'
                      }`}
                    >
                      <div className="font-medium">{t.label}</div>
                      <div className="text-[10px] text-theme-muted mt-0.5 line-clamp-1">
                        {t.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Relocation details */}
              {requestType === 'relocating' && (
                <div className="p-4 rounded-xl border border-indigo-500/30 bg-indigo-500/5 space-y-3">
                  <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wider">Relocation details</p>
                  <div>
                    <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-1.5">
                      New address
                    </label>
                    <input
                      type="text"
                      value={relocNewAddress}
                      onChange={(e) => setRelocNewAddress(e.target.value)}
                      placeholder="Where they're moving to"
                      className="w-full px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-1.5">
                        Move date
                      </label>
                      <input
                        type="date"
                        value={relocMoveDate}
                        onChange={(e) => setRelocMoveDate(e.target.value)}
                        className="w-full px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-1.5">
                        Who is moving
                      </label>
                      <input
                        type="text"
                        value={relocWho}
                        onChange={(e) => setRelocWho(e.target.value)}
                        placeholder="Account holder / tenant / contact"
                        className="w-full px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-1.5">
                      Old address plan
                    </label>
                    <input
                      type="text"
                      value={relocOldAddressPlan}
                      onChange={(e) => setRelocOldAddressPlan(e.target.value)}
                      placeholder="Disconnect / new occupant takes over / keep running"
                      className="w-full px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    />
                  </div>
                </div>
              )}

              {/* Account closure details */}
              {requestType === 'account_closure' && (
                <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/5 space-y-3">
                  <p className="text-xs font-semibold text-rose-400 uppercase tracking-wider">Closure details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor="sr-closure-date"
                        className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-1.5"
                      >
                        Closure date
                      </label>
                      <input
                        id="sr-closure-date"
                        type="date"
                        value={closureDate}
                        onChange={(e) => setClosureDate(e.target.value)}
                        className="w-full min-h-[44px] px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/50"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="sr-closure-reason"
                        className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-1.5"
                      >
                        Reason
                      </label>
                      <input
                        id="sr-closure-reason"
                        type="text"
                        value={closureReason}
                        onChange={(e) => setClosureReason(e.target.value)}
                        placeholder="Why the customer is leaving"
                        className="w-full min-h-[44px] px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/50"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-theme-muted">
                    Each person picked below gets an alert as soon as the request is created.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {SERVICE_REQUEST_CLOSURE_PARTS.map(({ key, label }) => (
                      <div key={key}>
                        <label
                          htmlFor={`sr-closure-owner-${key}`}
                          className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-1.5"
                        >
                          {label}
                        </label>
                        <select
                          id={`sr-closure-owner-${key}`}
                          value={closureOwners[key] ?? ''}
                          onChange={(e) =>
                            setClosureOwners((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          className="w-full min-h-[44px] px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/50"
                        >
                          <option value="">Nobody yet</option>
                          {closureCandidates.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                  Short Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Change NBN speed to 250/25"
                  className="w-full px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                  maxLength={120}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                  Description / Notes
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Full detail of what the customer wants and any relevant context..."
                  rows={4}
                  className="w-full px-3 py-2.5 bg-surface-input border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/50 resize-none"
                />
              </div>

              {/* Priority + Intake method */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                    Priority
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PRIORITIES.map((p) => {
                      const colors = SERVICE_REQUEST_PRIORITY_COLORS[p];
                      return (
                        <button
                          key={p}
                          onClick={() => setPriority(p)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                            priority === p
                              ? `${colors.badge} border-current`
                              : 'bg-surface-hover border-theme text-theme-muted hover:bg-surface-raised'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                          {SERVICE_REQUEST_PRIORITY_LABELS[p]}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                    Intake Method
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {INTAKE_METHOD_OPTIONS.map((opt) => {
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setIntakeMethod(opt.value)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                            intakeMethod === opt.value
                              ? 'bg-brand-secondary/15 border-brand-secondary text-theme-primary'
                              : 'bg-surface-hover border-theme text-theme-muted hover:bg-surface-raised'
                          }`}
                        >
                          <Icon className="w-3 h-3" />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-sm text-rose-600 dark:text-rose-300">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-theme">
              <button
                onClick={onClose}
                className="px-4 py-2.5 bg-surface-hover border border-theme rounded-lg text-sm text-theme-secondary hover:bg-surface-raised transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedCustomer || !title.trim() || !requestType}
                className="flex items-center gap-2 px-5 py-2.5 bg-brand-secondary text-brand-dark rounded-lg text-sm font-semibold hover:bg-brand-secondary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Create Request
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Add Customer Service modal — opens when user clicks "+ New Service".
          Deliberately OUTSIDE AnimatePresence: it owns its own visibility and
          isn't a motion child, and a second unkeyed child in there tripped
          React's duplicate-key warning on every render that had a customer
          selected (immediate when opened with preSelectedCustomerId). */}
      {selectedCustomer && hasCustomerServices && (
        <AddCustomerServiceModal
          isOpen={showAddServiceModal}
          onClose={() => setShowAddServiceModal(false)}
          customerId={selectedCustomer.id!}
          customerName={selectedCustomer.name || 'Unknown'}
          customerAddress={selectedCustomer.address}
          onServiceAdded={() => {
            setShowAddServiceModal(false);
            // Reload services + auto-select the newest one (sorted createdAt desc)
            if (selectedCustomer?.id) {
              loadServices(selectedCustomer.id).then((list) => {
                if (list.length > 0 && list[0].id) {
                  setSelectedServiceId(list[0].id);
                }
              });
            }
          }}
        />
      )}
    </>
  );
}
